// Developer mode (simple-mode D29/D30).
//
//   preference = GET /me/preferences      → developer_mode   (per user, server-side, D29)
//   entitled   = GET /entitlements        → features.developer_mode.value (per workspace plan)
//   effective  = preference && entitled                       (D30: the entitlement wins)
//
// THE one switch for developer-only UI. Developer pages plug in by being routed inside
// <RequireDeveloperMode> (see components/DeveloperRoute.tsx) and listed in
// DEVELOPER_NAV_GROUPS (components/nav.ts); in-page checks read `useMode().effective`.
//
// Fail closed: while loading, `effective` is false and `loading` is true (the route guard
// waits instead of redirecting); an error on either query leaves `effective` false.
// The entitlements query is keyed by workspace, so switching workspace re-reads it
// (switchWorkspace also clears the query cache). It is THE entitlements query from
// src/entitlements (one module, one key); `feature(key)` is that module's featureOn.
import { createContext, useCallback, useContext, useEffect, type ReactNode } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiFetch } from '../api/client'
import { qk } from '../api/queries'
import type { UserPreferences } from '../api/types'
import { featureOn, useEntitlements } from '../entitlements'
import { useSession } from './SessionContext'
import { applyTheme } from './theme'

export interface ModeState {
  /** Either query still in flight (no answer yet). */
  loading: boolean
  /** Either query failed; everything gated stays off. */
  error: unknown
  /** The user's stored Developer mode choice. */
  preference: boolean
  /** The active workspace's plan carries `developer_mode`. */
  entitled: boolean
  /** preference && entitled: developer pages render only when this is true. */
  effective: boolean
  /** The active workspace's plan carries `connect_claude` (Connect to Claude page). */
  connectClaude: boolean
  /** Any boolean entitlement key (false while loading or on error). */
  feature: (key: string) => boolean
  /** Persist the preference (PATCH /me/preferences {developer_mode}). */
  setDeveloperMode: (on: boolean) => Promise<void>
  /** A PATCH is in flight. */
  saving: boolean
  /** Re-run the failed query/queries. */
  retry: () => void
}

const ModeContext = createContext<ModeState | null>(null)

export function ModeProvider({ children }: { children: ReactNode }) {
  const { workspaceId } = useSession()
  const qc = useQueryClient()

  const prefs = useQuery({
    queryKey: qk.myPreferences,
    queryFn: () => apiFetch<UserPreferences>('/me/preferences'),
  })
  const ents = useEntitlements()
  // The same preferences read carries the theme (S3): reflect it on <html data-theme>
  // whenever it loads or a PATCH writes a new value into this cache entry.
  const theme = prefs.data?.theme
  useEffect(() => applyTheme(theme), [theme])

  const save = useMutation({
    mutationFn: (on: boolean) =>
      apiFetch<UserPreferences>('/me/preferences', {
        method: 'PATCH',
        body: JSON.stringify({ developer_mode: on }),
      }),
    onSuccess: (data) => qc.setQueryData(qk.myPreferences, data),
  })
  const { mutateAsync } = save
  const setDeveloperMode = useCallback(async (on: boolean) => {
    await mutateAsync(on)
  }, [mutateAsync])

  const preference = prefs.data?.developer_mode === true
  const entitled = featureOn(ents.data, 'developer_mode')
  const { refetch: refetchPrefs } = prefs
  const { refetch: refetchEnts } = ents

  const value: ModeState = {
    loading: prefs.isPending || (ents.isPending && !!workspaceId),
    error: prefs.error ?? ents.error ?? null,
    preference,
    entitled,
    effective: preference && entitled,
    connectClaude: featureOn(ents.data, 'connect_claude'),
    feature: (key) => featureOn(ents.data, key),
    setDeveloperMode,
    saving: save.isPending,
    retry: () => {
      if (prefs.error) void refetchPrefs()
      if (ents.error) void refetchEnts()
    },
  }

  return <ModeContext.Provider value={value}>{children}</ModeContext.Provider>
}

// eslint-disable-next-line react-refresh/only-export-components
export function useMode(): ModeState {
  const ctx = useContext(ModeContext)
  if (!ctx) throw new Error('useMode must be used within ModeProvider')
  return ctx
}
